<script setup lang="ts">
import { computed } from 'vue'
import type { Image, Action } from '../types'
import { cn } from '@/lib/utils'
import { openSafeInNewTab, toSafeMediaUrl } from '@/lib/urlSafety'
import { spacingClasses, imageAlignClasses, imageSizeClasses } from '../utils/classes'
import { useValidation } from '../composables/useValidation'
import { useCardContext } from '../composables/useCardContext'

const props = defineProps<{
  element: Image
  onAction?: (action: Action, inputs: Record<string, string>) => void
  sizeOverride?: Image['size']
}>()

const { elements } = useCardContext()
const { validate, inputs } = useValidation(() => elements)

const effectiveSize = computed(() => props.sizeOverride ?? props.element.size ?? 'auto')

const customStyle = computed(() => {
  const style: Record<string, string> = {}
  if (props.element.width) {
    style.width = props.element.width.endsWith('px') ? props.element.width : `${props.element.width}px`
  }
  if (props.element.height) {
    style.height = props.element.height.endsWith('px') ? props.element.height : `${props.element.height}px`
  }
  return style
})

const hasSelectAction = computed(() => !!props.element.selectAction)
const safeImageUrl = computed(() => toSafeMediaUrl(props.element.url))

function handleClick() {
  if (!props.element.selectAction || !props.onAction) return
  
  if (props.element.selectAction.type === 'Action.OpenUrl') {
    openSafeInNewTab(props.element.selectAction.url)
    return
  }

  if (!validate()) return
  
  props.onAction(props.element.selectAction, { ...inputs })
}
</script>

<template>
  <img
    :src="safeImageUrl"
    :alt="props.element.altText ?? ''"
    :style="customStyle"
    :class="cn(
      'block',
      imageSizeClasses[effectiveSize],
      spacingClasses[props.element.spacing ?? 'default'],
      imageAlignClasses[props.element.horizontalAlignment ?? 'left'],
      hasSelectAction && 'cursor-pointer hover:opacity-80 transition-opacity'
    )"
    @click="handleClick"
  />
</template>
